import React from 'react'
import PropTypes from 'prop-types'
import { Flex, Link, Paragraph, Table } from 'components/patterns'
import {
  ModalBody,
  ModalButton,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalMainContainer,
  ModalMedium,
} from 'components/patterns/Modal'
import DynamicFeatureIllustration from 'img/dynamic-feature.svg'
import InfoIcon from 'img/info-icon-dual-tone.svg'
import strings from 'utils/strings'
import { bitBytePairLabel, getTheRange } from 'components/feature/utils/bitGrid'
import {
  CountText,
  OutputNameText,
  SourceFeaturesTitle,
  StyledHeader,
} from './DynamicFeatureDetailsView.style'

const getOutputName = (opItem) => {
  const {
    outputType,
    outputId,
    startBit,
    numberOfBits,
    outputConnector,
    outputLocation,
    outputDescription,
  } = opItem
  if (outputType.toUpperCase() === 'CAN') {
    const getRange = getTheRange(numberOfBits)
    const selectedRange = getRange(startBit)
    const { startBit: bitStart, endBit } = selectedRange
    return `${bitBytePairLabel(bitStart, endBit)}: ${outputId}`
  }
  if (outputType.toUpperCase() === 'PIN') {
    return `${strings.connector} ${outputConnector}: ${strings.pin} ${outputLocation}`
  }
  return outputDescription
}

const DynamicFeatureDetailsView = ({ onClose, featureDetails }) => {
  const outputType = featureDetails.dynamicFeatures.flatMap(
    (item) => item.outputType
  )[0]

  let opTypeStr =
    outputType.toUpperCase() === 'PIN' ? strings.pin : strings.native

  if (outputType.toUpperCase() === 'CAN') {
    opTypeStr = strings.twoBitCan
  }

  return (
    <ModalMedium>
      <ModalHeader>
        <StyledHeader type="h2">
          <img alt="Info icon" src={InfoIcon} />
          {strings.dynamic_feature_modal_title(
            strings[featureDetails.moduleName.toLowerCase()],
            opTypeStr
          )}
        </StyledHeader>
        <ModalCloseButton onClick={onClose} />
      </ModalHeader>
      <ModalBody>
        <ModalMainContainer>
          <ModalContent>
            <Flex flexDirection="column" gap="16px">
              <Paragraph>{strings.dynamic_feature_modal_body_text}</Paragraph>
              <img
                alt="Dynamic Feature Illustration"
                src={DynamicFeatureIllustration}
                width={516}
                style={{ margin: '0 auto' }}
              />
              <SourceFeaturesTitle>
                {strings.dynamic_feature_source_features_title(
                  featureDetails.partNumber
                )}
              </SourceFeaturesTitle>
              {featureDetails.dynamicFeatures?.map((item) => (
                <Flex
                  key={item.outputDescription}
                  flexDirection="column"
                  gap="8px"
                >
                  <Flex justifyContent="space-between" flex={1}>
                    <OutputNameText>{getOutputName(item)}</OutputNameText>
                    <OutputNameText>
                      {strings.logic_sets}:{' '}
                      <CountText>{item.complexQueryCount}</CountText>
                    </OutputNameText>
                  </Flex>
                  <Table
                    columns={[
                      {
                        title: strings.feature_no,
                        dataIndex: 'partNumber',
                        render: (partNumber, record) => (
                          <Link
                            target="_blank"
                            rel="noopener noreferrer"
                            to={`/features/${record.id}`}
                          >
                            {partNumber}
                          </Link>
                        ),
                        width: 160,
                      },
                      {
                        title: strings.description,
                        dataIndex: 'description',
                      },
                    ]}
                    dataSource={item.features}
                  />
                </Flex>
              ))}
            </Flex>
          </ModalContent>
        </ModalMainContainer>
      </ModalBody>
      <ModalFooter>
        <ModalButton type="secondary" onClick={onClose}>
          {strings.close}
        </ModalButton>
      </ModalFooter>
    </ModalMedium>
  )
}

DynamicFeatureDetailsView.propTypes = {
  onClose: PropTypes.func.isRequired,
  featureDetails: PropTypes.shape({
    moduleName: PropTypes.string,
    partNumber: PropTypes.string,
    dynamicFeatures: PropTypes.arrayOf(
      PropTypes.objectOf(
        PropTypes.shape({
          outputDescription: PropTypes.string,
          complexQueryCount: PropTypes.string,
          features: PropTypes.arrayOf(
            PropTypes.objectOf(
              PropTypes.shape({
                partNumber: PropTypes.string,
                description: PropTypes.string,
              })
            )
          ),
        })
      )
    ),
  }).isRequired,
}

export default DynamicFeatureDetailsView
